<script setup>
import { onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useAnnouncements } from "../../Composables/useAnnouncements.js"
import { useAnnouncementStore } from "../../Store/AnnouncementStore.js"
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()
const announcementStore = useAnnouncementStore()

const storageKey = "Shiftech_AnnouncementList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: announcements,
    links,
    meta,
    loading,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    loadData,
} = useDataManager({
    baseUrl: '/api/announcements',
    defaultSortingField: 'created_at',
    defaultSortingFieldsOrder: {
        created_at: 'desc',
        type: 'asc',
        is_active: 'desc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const columns = ref([
    { label: '#ID', slot: 'id', class: 'col-1' },
    { label: 'Message', slot: 'message', class: 'col-4' },
    { label: 'Type', slot: 'type', field: 'type', sortable: true, class: 'col-1' },
    { label: 'Cibles', slot: 'targets', class: 'col-2' },
    { label: 'Période', slot: 'period', class: 'col-2' },
    { label: 'Actif', slot: 'active', field: 'is_active', sortable: true, class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        url.value = savedSettings.url || url.value
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
    }

    loadData(url.value)
})

watch(
    [url, sortingField, sortingFieldsOrder],
    ([newUrl, newSortingField, newSortingOrder]) => {
        saveToLocalStorage({
            url: newUrl,
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const idToDelete = ref(null)

function showModal(modalId, id = null) {
    if (modalId === 'modalDelete') {
        idToDelete.value = id
    }
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const { deleteAnnouncement } = useAnnouncements({
    onDeleted: () => {
        loadData(url.value)
        announcementStore.fetchActiveBanners()
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})

function targetLabel(item) {
    const centers = item.data.centers?.length ?? 0
    const users = item.data.users?.length ?? 0
    if (centers === 0 && users === 0) {
        return 'Tout le monde'
    }
    const parts = []
    if (centers > 0) {
        parts.push(`${centers} centre${centers > 1 ? 's' : ''}`)
    }
    if (users > 0) {
        parts.push(`${users} utilisateur${users > 1 ? 's' : ''}`)
    }
    return parts.join(' + ')
}

function periodLabel(item) {
    const start = item.data.starts_at_fr
    const end = item.data.ends_at_fr
    if (!start && !end) {
        return 'Toujours'
    }
    return `${start ?? '…'} → ${end ?? '…'}`
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header border border-dashed border-top-0 border-start-0 border-end-0">
                    <div class="d-flex align-items-center">
                        <h5 class="card-title mb-0 flex-grow-1">Tous les bandeaux d'annonce</h5>
                        <router-link :to="{ name:'announcements.create' }" class="btn btn-secondary">
                            <i class="ri-add-fill me-1 align-bottom hide-on-mobile"></i> Ajouter
                        </router-link>
                    </div>
                </div>
                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="announcements"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <template #message="{ item }">
                            <div class="text-truncate" style="max-width: 460px;" :title="item.data.message">
                                <span class="fw-medium text-dark" v-if="item.data.title">{{ item.data.title }} : </span>
                                <span class="text-muted">{{ item.data.message }}</span>
                            </div>
                        </template>

                        <template #type="{ item }">
                            <span class="badge" :class="`bg-${item.data.type_color}-subtle text-${item.data.type_color}`">
                                {{ item.data.type_label }}
                            </span>
                        </template>

                        <template #targets="{ item }">
                            {{ targetLabel(item) }}
                        </template>

                        <template #period="{ item }">
                            {{ periodLabel(item) }}
                        </template>

                        <template #active="{ item }">
                            <span class="badge bg-success-subtle text-success" v-if="item.data.is_active">Oui</span>
                            <span class="badge bg-danger-subtle text-danger" v-else>Non</span>
                        </template>

                        <template #actions="{ item }">
                            <router-link :to="{ name: 'announcements.edit', params: { id: item.data.id } }" class="text-info" title="Modifier le bandeau" v-if="item.can.update">
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <a href="#" class="text-danger" title="Supprimer le bandeau" @click.prevent="showModal('modalDelete', item.data.id)" v-if="item.can.delete">
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i class="ri-delete-bin-3-fill fs-17 px-1 text-muted" v-else></i>
                        </template>
                    </data-table>

                    <modal id="modalDelete" title="Supprimer le bandeau ?" btn-class="btn-danger" btn-text="Supprimer" @click:button="deleteAnnouncement(idToDelete)">
                        <template #icon>
                            <lord-icon src="https://cdn.lordicon.com/gsqxdxog.json" trigger="loop" colors="primary:#212529,secondary:#f06548" style="width:90px;height:90px"></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">Vous êtes sur le point de supprimer un bandeau d'annonce. Continuer ?</p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
